import { useState } from 'react';
import Button from '../../../shared/components/Button';
import { Modal } from '../../../shared/components/Modal';
import { formatPrice } from '../../../shared/utils/priceUtils';
import {
  useCreateSalesFeature,
  useDeactivateSalesFeature,
  useSalesFeatures,
  useUpdateSalesFeature,
} from '../hooks/useSales';
import {
  BUSINESS_IMPACT_LABELS,
  BusinessImpact,
  FEATURE_CATEGORY_LABELS,
  SalesFeatureCategory,
  type CreateSalesFeaturePayload,
  type SalesFeature,
} from '../types';

/** Naira-denominated money input that stores kobo. */
function MoneyField({
  label,
  valueMinor,
  onChange,
}: {
  label: string;
  valueMinor: number;
  onChange: (minor: number) => void;
}) {
  return (
    <label className="block">
      <span className="text-[13px] font-medium text-foreground">{label}</span>
      <div className="mt-1 flex items-center gap-2">
        <span className="text-sm text-muted-foreground">₦</span>
        <input
          type="number"
          min={0}
          step="0.01"
          value={valueMinor / 100}
          onChange={(e) => {
            const naira = Number(e.target.value);
            if (Number.isNaN(naira)) return;
            // Round to whole kobo — the backend validates @IsInt.
            onChange(Math.max(0, Math.round(naira * 100)));
          }}
          className="flex-1 h-[40px] rounded-[8px] border border-border bg-background px-3 text-sm"
        />
      </div>
    </label>
  );
}

const EMPTY: CreateSalesFeaturePayload = {
  name: '',
  category: SalesFeatureCategory.CORE,
  developmentCostMinor: 0,
  businessImpact: BusinessImpact.MEDIUM,
  annualPlatformFeeMinor: 0,
};

function FeatureFormModal({
  isOpen,
  feature,
  onClose,
  onSubmit,
  isSaving,
}: {
  isOpen: boolean;
  feature: SalesFeature | null;
  onClose: () => void;
  onSubmit: (values: CreateSalesFeaturePayload) => Promise<void>;
  isSaving: boolean;
}) {
  const [values, setValues] = useState<CreateSalesFeaturePayload>(
    feature
      ? {
          name: feature.name,
          category: feature.category,
          developmentCostMinor: feature.developmentCostMinor,
          businessImpact: feature.businessImpact,
          annualPlatformFeeMinor: feature.annualPlatformFeeMinor,
          displayOrder: feature.displayOrder ?? undefined,
        }
      : EMPTY,
  );

  const set = <K extends keyof CreateSalesFeaturePayload>(
    key: K,
    value: CreateSalesFeaturePayload[K],
  ) => setValues((v) => ({ ...v, [key]: value }));

  const canSubmit = values.name.trim().length > 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <div className="p-6">
        <h2 className="text-[18px] font-semibold text-foreground">
          {feature ? 'Edit feature' : 'Add feature'}
        </h2>
        <p className="text-[13px] text-muted-foreground mt-1">
          Feeds the calculator's price book. Costs are stored in kobo.
        </p>

        <div className="mt-5 space-y-4">
          <label className="block">
            <span className="text-[13px] font-medium text-foreground">Name</span>
            <input
              value={values.name}
              onChange={(e) => set('name', e.target.value)}
              className="mt-1 w-full h-[40px] rounded-[8px] border border-border bg-background px-3 text-sm"
            />
          </label>

          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="text-[13px] font-medium text-foreground">Category</span>
              <select
                value={values.category}
                onChange={(e) => set('category', e.target.value as SalesFeatureCategory)}
                className="mt-1 w-full h-[40px] rounded-[8px] border border-border bg-background px-3 text-sm"
              >
                {(Object.keys(FEATURE_CATEGORY_LABELS) as SalesFeatureCategory[]).map((c) => (
                  <option key={c} value={c}>
                    {FEATURE_CATEGORY_LABELS[c]}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[13px] font-medium text-foreground">Business impact</span>
              <select
                value={values.businessImpact}
                onChange={(e) => set('businessImpact', e.target.value as BusinessImpact)}
                className="mt-1 w-full h-[40px] rounded-[8px] border border-border bg-background px-3 text-sm"
              >
                {(Object.keys(BUSINESS_IMPACT_LABELS) as BusinessImpact[]).map((b) => (
                  <option key={b} value={b}>
                    {BUSINESS_IMPACT_LABELS[b]}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <MoneyField
              label="Development cost"
              valueMinor={values.developmentCostMinor}
              onChange={(v) => set('developmentCostMinor', v)}
            />
            <MoneyField
              label="Annual platform fee"
              valueMinor={values.annualPlatformFeeMinor}
              onChange={(v) => set('annualPlatformFeeMinor', v)}
            />
          </div>

          <label className="block">
            <span className="text-[13px] font-medium text-foreground">Display order</span>
            <input
              type="number"
              value={values.displayOrder ?? ''}
              onChange={(e) =>
                set('displayOrder', e.target.value === '' ? undefined : Number(e.target.value))
              }
              className="mt-1 w-full h-[40px] rounded-[8px] border border-border bg-background px-3 text-sm"
            />
          </label>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} className="h-[40px]">
            Cancel
          </Button>
          <Button
            className="h-[40px]"
            disabled={!canSubmit || isSaving}
            loading={isSaving}
            onClick={() => onSubmit(values)}
          >
            {feature ? 'Save changes' : 'Add feature'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export function FeatureCatalogPanel({ canManage }: { canManage: boolean }) {
  const { data: features = [], isLoading, error } = useSalesFeatures();
  const create = useCreateSalesFeature();
  const update = useUpdateSalesFeature();
  const deactivate = useDeactivateSalesFeature();

  // undefined = closed, null = create, feature = edit.
  const [editing, setEditing] = useState<SalesFeature | null | undefined>(undefined);

  const handleSubmit = async (values: CreateSalesFeaturePayload) => {
    if (editing) {
      await update.mutateAsync({ id: editing.id, data: values });
    } else {
      await create.mutateAsync(values);
    }
    setEditing(undefined);
  };

  if (isLoading) return <div className="text-center py-16 text-muted-foreground">Loading price book…</div>;
  if (error)
    return <div className="text-center py-16 text-red-500">Failed to load the price book.</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-[13px] text-muted-foreground">
          The catalogue the calculator prices against. Deactivated features stay on existing
          quotes but can't be added to new ones.
        </p>
        {canManage && (
          <Button size="sm" className="h-[40px] shrink-0" onClick={() => setEditing(null)}>
            Add feature
          </Button>
        )}
      </div>

      {features.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground text-[14px] border border-dashed border-[#D9D9D9] rounded-[16px]">
          No features yet.{canManage && ' Click "Add feature" to start the price book.'}
        </div>
      ) : (
        <div className="bg-card rounded-2xl border border-border overflow-x-auto">
          <table className="w-full min-w-[760px]">
            <thead>
              <tr className="border-b border-border bg-muted text-left">
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Feature</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Category</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Impact</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Build cost</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Annual fee</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</th>
                {canManage && (
                  <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {features.map((f) => (
                <tr key={f.id} className="hover:bg-muted transition-colors align-middle">
                  <td className="px-6 py-4 text-sm font-medium text-foreground">{f.name}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {FEATURE_CATEGORY_LABELS[f.category] ?? f.category}
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {BUSINESS_IMPACT_LABELS[f.businessImpact] ?? f.businessImpact}
                  </td>
                  <td className="px-6 py-4 text-sm text-foreground tabular-nums">
                    {formatPrice(f.developmentCostMinor)}
                  </td>
                  <td className="px-6 py-4 text-sm text-foreground tabular-nums">
                    {formatPrice(f.annualPlatformFeeMinor)}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`text-[11px] font-[600] px-2 py-0.5 rounded-full ${
                        f.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {f.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  {canManage && (
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => setEditing(f)}
                          className="text-xs text-blue-600 hover:underline"
                        >
                          Edit
                        </button>
                        {f.isActive ? (
                          <button
                            onClick={() => deactivate.mutate(f.id)}
                            disabled={deactivate.isPending}
                            className="text-xs text-red-600 hover:underline disabled:opacity-50"
                          >
                            Deactivate
                          </button>
                        ) : (
                          // Without this a deactivated feature is a dead end — there
                          // is no other path back to active, and the edit form never
                          // sends isActive.
                          <button
                            onClick={() => update.mutate({ id: f.id, data: { isActive: true } })}
                            disabled={update.isPending}
                            className="text-xs text-emerald-700 hover:underline disabled:opacity-50"
                          >
                            Activate
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== undefined && (
        <FeatureFormModal
          // Remount per target so the form seeds from the right feature.
          key={editing?.id ?? 'new'}
          isOpen
          feature={editing}
          onClose={() => setEditing(undefined)}
          onSubmit={handleSubmit}
          isSaving={create.isPending || update.isPending}
        />
      )}
    </div>
  );
}
